import React from 'react';
import { PageHeader } from '@keplerco/core';
import { CompanyInfoPage } from './company-info/company-info.page';
import { InterestsPage } from './interests/interests.page';
import { DescriptionPage } from './description/description.page';
import { SummaryPage } from './summary/summary.page';
import { OnboardingUsersPreviewPage } from './users/onboarding-users-preview.page';
import { useStepperConnector } from '../../../components/general/stepper/use-stepper-connector';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import { PagePath } from '../../../navigation/navigation.enums';
import { Stepper } from '../../../components/general/stepper/stepper';
import { Step } from '../../../components/general/stepper/step/step';
import styles from './freemium.onboarding.module.css';
import { useNavigate } from 'react-router';
import { useAppState } from '../../../overmind';
import { YourInfoPage } from './your-info/your-info.page';

/**
 * Layout component for the onboarding wizard
 * Provides common UI elements and navigation for the onboarding process
 * Uses the Stepper component for improved navigation and layout
 */
export function FreemiumOnboardingLayout(): JSX.Element {
  const navigate = useNavigate();
  const { freemiumAccount } = useAppState();
  const { user } = useAppState();

  const stepperConnector = useStepperConnector();

  // Step IDs for the onboarding process
  const StepIds = {
    YourInfo: 'Your Information',
    CompanyInfo: 'Company Information',
    Interests: 'Interests',
    Description: 'Company Description',
    UploadUsers: 'Upload Users',
    Summary: 'Summary'
  };

  return (
    <div className={styles.background} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className="wrapper stack" style={{ paddingBottom: 24, gap: 24 }}>
        <PageHeader
          breadcrumbs={[
            { name: 'Onboarding', url: PagePath.onboarding }
          ]}
          title="Set up your Kepler Account"
          subtitle="Let's create your roles and skills taxonomy in a few easy steps"
          divider
        />

        <Stepper {...stepperConnector}>
          <Step key={StepIds.YourInfo} completed={!!user?.jobTitle} label={StepIds.YourInfo} required>
            <YourInfoPage onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.CompanyInfo} completed={!!freemiumAccount?.companyName} label={StepIds.CompanyInfo} required>
            <CompanyInfoPage onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.Interests}
            completed={(freemiumAccount?.departmentInterests?.length ?? 0) > 0 ||
              (freemiumAccount?.roleInterests?.length ?? 0) > 0 ||
              (freemiumAccount?.skillInterests?.length ?? 0) > 0}
            label={StepIds.Interests}
            required>
            <InterestsPage onBack={stepperConnector.previous} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.Description} completed={!!freemiumAccount?.companyDescription} label={StepIds.Description} required>
            <DescriptionPage onBack={stepperConnector.previous} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.UploadUsers} label={StepIds.UploadUsers} completed={!!freemiumAccount?.progress?.hasUploadedUsers} required>
            <OnboardingUsersPreviewPage onBack={stepperConnector.previous} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.Summary} label={StepIds.Summary} required>
            <SummaryPage onBack={stepperConnector.previous} onComplete={() => navigate(PagePath.dashboard)} />
          </Step>
        </Stepper>
      </div>
    </div>
  );
}

